import { Cable } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Alert, Button, EmptyState, Spinner } from '@/components/ui'

import { useIntegrations } from '../queries'
import { IntegrationConnectorCard } from './IntegrationConnectorCard'

export interface IntegrationsListProps {
  readonly tenantId: string
}

/**
 * Lista os conectores do tenant em cards. Estados: carregando (Spinner), erro (Alert + tentar de
 * novo), vazio (EmptyState honesto + nota discreta de roadmap) e a lista de cards. Read-only hoje:
 * nao ha acao de conectar; conectar sistemas proprios faz parte do roadmap do produto.
 */
export function IntegrationsList({ tenantId }: IntegrationsListProps) {
  const { t } = useTranslation()
  const integrationsQuery = useIntegrations(tenantId)

  if (integrationsQuery.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t('integrations.loading')} />
      </div>
    )
  }

  if (integrationsQuery.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert variant="danger" title={t('integrations.loadError')} />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void integrationsQuery.refetch()
          }}
        >
          {t('retry')}
        </Button>
      </div>
    )
  }

  if (integrationsQuery.data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3">
        <EmptyState
          icon={Cable}
          title={t('integrations.empty.title')}
          description={t('integrations.empty.description')}
          action={
            <Button
              variant="secondary"
              loading={integrationsQuery.isRefetching}
              onClick={() => {
                void integrationsQuery.refetch()
              }}
            >
              {t('integrations.empty.refresh')}
            </Button>
          }
        />
        <p className="max-w-md text-center text-xs text-foreground-muted">
          {t('integrations.empty.roadmapNote')}
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {integrationsQuery.data.map((connector) => (
        <IntegrationConnectorCard key={connector.key} connector={connector} />
      ))}
    </div>
  )
}
